import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BehaviorSubject, combineLatestWith, Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { select } from 'd3';
import { Selection } from 'd3-selection';

import { Breakpoint, DataRow } from '@fcom/common/interfaces';
import { getCurrentBreakPoint } from '@fcom/common/utils/layout.utils';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { snapshot } from '@fcom/rx/utils/snapshot';
import { DateFormat, Pattern } from '@fcom/core/utils';
import { toISOStringPreserveTimeZone } from '@fcom/common-booking/utils/date-utils';

import { UpdatableChart } from './updatable-chart.interface';
import { ChartMargin, DATA_CLASS_NAME } from './constants';
import { EMPTY_TOOLTIP, TooltipProps } from '../../tooltip/tooltip';
import { TOOLTIP_PADDING } from '../../line-chart/constants';

@Component({ template: '', changeDetection: ChangeDetectionStrategy.OnPush })
export abstract class AbstractChartComponent implements UpdatableChart, OnInit, OnDestroy {
  destroyRef = inject(DestroyRef);

  @HostBinding('class') classAttribute = 'block fill';

  @Input()
  height$: BehaviorSubject<unknown>;

  @Input()
  dynamicHeight: boolean;

  @Input()
  width$: BehaviorSubject<unknown>;

  @Input()
  dynamicWidth: boolean;

  @Input({ required: true })
  rows$: Observable<DataRow[]>;

  @Input()
  margin: ChartMargin = { top: 20, right: 20, bottom: 50, left: 40 };

  @Input()
  transform: string;

  @Input()
  preserveAspectRatio: string;

  @Input()
  viewBox: string;

  @Input()
  unit: string;

  @Input()
  tiltXAxisText = false;

  @Input()
  isMobile = false;

  @Input()
  legendText$: Observable<string>;

  tooltip$ = new BehaviorSubject<TooltipProps>(EMPTY_TOOLTIP);

  ref = inject(ChangeDetectorRef);

  // single resize observer for all instances of this component for performance reasons
  // https://github.com/WICG/resize-observer/issues/59#issuecomment-408098151
  resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
    entries.forEach((entry: ResizeObserverEntry) => {
      this.resetDimensions(entry.contentRect.width, entry.contentRect.height);
    });
  });

  constructor(
    protected elementRef: ElementRef,
    protected languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.resetDimensions(undefined, undefined);
    this.applyChartMargins();
    this.rows$
      .pipe(
        filter(Boolean),
        combineLatestWith(this.width$, this.height$),
        tap(([rows, width, height]) => {
          const svgGroup: Selection<SVGGElement, unknown, null, undefined> = this.selectSVG().select('g');
          const line = svgGroup.selectAll(`.${DATA_CLASS_NAME}`);

          if (line.size() === 0) {
            this.draw(
              svgGroup,
              rows,
              this.innerWidth(width as number),
              this.innerHeight(height as number),
              getCurrentBreakPoint()
            );
          } else {
            this.update(
              svgGroup,
              rows,
              this.innerWidth(width as number),
              this.innerHeight(height as number),
              getCurrentBreakPoint()
            );
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  public innerWidth(width: number): number {
    return width - this.margin.left - this.margin.right;
  }

  public innerHeight(height: number): number {
    return height - this.margin.top - this.margin.bottom;
  }

  public resetDimensions(newWidth: number | undefined, newHeight: number | undefined): void {
    if (this.dynamicWidth) {
      if (this.width$) {
        this.width$.next(newWidth ?? this.elementRef.nativeElement.offsetWidth);
      } else {
        this.width$ = new BehaviorSubject(newWidth ?? this.elementRef.nativeElement.offsetWidth);
      }
    }

    if (this.dynamicHeight) {
      if (this.height$) {
        this.height$.next(newHeight ?? this.elementRef.nativeElement.offsetHeight);
      } else {
        this.height$ = new BehaviorSubject(newHeight ?? this.elementRef.nativeElement.offsetHeight);
      }
    }
  }

  private applyChartMargins(): void {
    this.selectSVG().append('g').attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);
  }

  private selectSVG(): Selection<SVGElement, unknown, null, undefined> {
    return select(this.elementRef.nativeElement).select('svg');
  }

  protected getTooltipTop(e, averageHeight: number): string {
    if (e.layerY > (this.height$.value as number) / 2) {
      return `${e.pageY - averageHeight}px`;
    }
    return `${e.pageY + TOOLTIP_PADDING}px`;
  }

  protected getTooltipLeft(e: MouseEvent, averageWidth: number): string {
    if (e.layerX > (this.width$.value as number) / 2) {
      return `${e.pageX - averageWidth}px`;
    }
    return `${e.pageX + TOOLTIP_PADDING}px`;
  }

  protected translateXValue(xValue: string): string {
    return snapshot(
      this.languageService.translate('date').pipe(
        map((translations: any) => {
          if (!xValue) {
            return '';
          }
          return new DateFormat(translations).format(toISOStringPreserveTimeZone(xValue), new Pattern('MMM'), false);
        })
      )
    );
  }

  abstract draw(
    svgGroup: Selection<SVGGElement, unknown, null, undefined>,
    rows: DataRow[],
    width: number,
    height: number,
    breakpoint: Breakpoint
  ): void;
  abstract update(
    svgGroup: Selection<SVGGElement, unknown, null, undefined>,
    rows: DataRow[],
    width: number,
    height: number,
    breakpoint: Breakpoint
  ): void;
}
